export interface ReviewContext {
  type: 'selected' | 'all_pending'
  invoiceIds: string[]
}

export interface ReviewSession {
  id: string
  context: ReviewContext
  createdAt: number
  expiresAt: number
  initialTotal: number
  completedCount: number
}

const REVIEW_SESSIONS_KEY = 'review_sessions'

export const getStoredReviewSessions = (): Record<string, ReviewSession> => {
  try {
    const stored = localStorage.getItem(REVIEW_SESSIONS_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

const cleanupExpiredSessions = (sessions: Record<string, ReviewSession>): void => {
  const now = Date.now()
  Object.keys(sessions).forEach(sessionId => {
    if (sessions[sessionId].expiresAt < now) {
      delete sessions[sessionId]
    }
  })
}

export const createReviewSession = (context: ReviewContext): string => {
  const sessionId = `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const session: ReviewSession = {
    id: sessionId,
    context,
    createdAt: Date.now(),
    expiresAt: Date.now() + (24 * 60 * 60 * 1000),
    initialTotal: context.invoiceIds.length,
    completedCount: 0
  }

  const existingSessions = getStoredReviewSessions()
  existingSessions[sessionId] = session
  cleanupExpiredSessions(existingSessions)
  localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(existingSessions))

  return sessionId
}

export const getReviewSession = (sessionId: string): ReviewSession | null => {
  const sessions = getStoredReviewSessions()
  const session = sessions[sessionId]

  if (!session || session.expiresAt < Date.now()) {
    if (session) {
      delete sessions[sessionId]
      localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(sessions))
    }
    return null
  }

  return session
}

export const initializeReviewSessions = (): void => {
  try {
    const sessions = getStoredReviewSessions()
    const cleanedSessions = { ...sessions }
    cleanupExpiredSessions(cleanedSessions)

    if (Object.keys(cleanedSessions).length !== Object.keys(sessions).length) {
      localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(cleanedSessions))
    }
  } catch (error) {
    console.warn('Failed to initialize review sessions:', error)
  }
}
